import React from "react";
import { Shield, Award, Gem, Sparkles } from "lucide-react";

/**
 * TrustBadges — composable badge row for a TREL Concierge.
 *
 * Trust tier colors (brand-aligned):
 *   Identity Verified   → Ocean AI Blue (#00AEEF)
 *   TREL Certified       → Deep Emerald  (#064E3B)
 *   TREL Elite Concierge → Champagne Gold (#C9A45C)
 *   TREL+ Member         → Champagne Gold gradient (legacy)
 *
 * Pass a `provider` object (or any object with the trust booleans).
 * Set `compact` for card-style placements (smaller pills, no label words).
 */

const TIP_IDENTITY = "This Concierge completed identity verification through a secure verification provider.";
const TIP_CERTIFIED = "This Concierge completed TREL's premium training and standards agreement.";
const TIP_ELITE = "Elite Concierges completed enhanced background screening and meet TREL's highest trust standards.";
const TIP_PLUS = "TREL+ Concierge — active premium membership for enhanced visibility and AI tools.";

export default function TrustBadges({ provider = {}, size = "sm", className = "", showPlus = true }) {
  const identity = !!provider.identity_verified;
  // certified flag is true when either explicit boolean or certification.completed_at exists
  const certified =
    !!provider.trel_certified ||
    !!(provider.certification && provider.certification.completed_at) ||
    provider.verification_status === "Approved";
  const elite = !!provider.elite_badge_active;
  const plus = !!provider.trel_plus_active;

  const dims = size === "md" ? "text-xs px-2.5 py-1" : "text-[10px] px-2 py-0.5";
  const ic = size === "md" ? 11 : 10;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="trust-badges">
      {elite && (
        <span
          title={TIP_ELITE}
          aria-label={TIP_ELITE}
          data-testid="badge-elite"
          className={`rounded-full inline-flex items-center gap-1 cursor-help font-semibold bg-gradient-to-r from-[#C9A45C] to-[#E0BE7A] text-[#071A2D] ${dims}`}
        >
          <Gem size={ic} /> TREL Elite
        </span>
      )}
      {certified && (
        <span
          title={TIP_CERTIFIED}
          aria-label={TIP_CERTIFIED}
          data-testid="badge-certified"
          className={`rounded-full inline-flex items-center gap-1 cursor-help bg-[#064E3B] text-[#FFF8EE] ${dims}`}
        >
          <Award size={ic} /> TREL Certified
        </span>
      )}
      {identity && (
        <span
          title={TIP_IDENTITY}
          aria-label={TIP_IDENTITY}
          data-testid="badge-identity"
          className={`rounded-full inline-flex items-center gap-1 cursor-help bg-[#00AEEF] text-[#FFF8EE] ${dims}`}
        >
          <Shield size={ic} /> Identity Verified
        </span>
      )}
      {showPlus && plus && (
        <span
          title={TIP_PLUS}
          aria-label={TIP_PLUS}
          data-testid="badge-trel-plus"
          className={`verified-gold rounded-full inline-flex items-center gap-1 cursor-help font-semibold ${dims}`}
        >
          <Sparkles size={ic} /> TREL+
        </span>
      )}
    </div>
  );
}

export { TIP_IDENTITY, TIP_CERTIFIED, TIP_ELITE, TIP_PLUS };
